/**
 * QueryBar — the Explore query primitive, on CodeMirror 6.
 *
 * Completed clauses render as atomic inline pill widgets: the caret treats each
 * as one unit (arrows skip it, ⌫ deletes it whole), click a pill to edit it in
 * place, and the clause you're CURRENTLY typing stays live text until the caret
 * leaves it. Native selection / copy / undo / IME come from CodeMirror.
 *
 *  - two-mode autocomplete (FIELD names on a bare token; VALUES inside a
 *    `field:`/`field!=` clause via the injected async `suggestValues`) — handled
 *    by @codemirror/autocomplete, reusing the lib's pure helpers.
 *  - implied precedence: clauses in an AND-run that's an operand of an OR get a
 *    dotted-green pill (AND binds tighter than OR), from `impliedGroupRanges`.
 *  - inline diagnostics from parse + compile.
 *
 * Controlled: parent owns the string (`value` / `onChange`); the surface
 * debounces it for the fetch, so the query applies as you (pause) typing.
 * `suggestValues` is injected (not imported) so this component stays free of the
 * analytics-client deps and node-test-safe; the real one is queryLangSuggest.js.
 */
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import { phAttrs } from "../lib/analytics/attrs.js";
import { ACTION, ATTRIBUTE } from "../lib/analytics/surfaceVocabulary.js";
import { EditorState, Facet } from "@codemirror/state";
import { EditorView, Decoration, WidgetType, ViewPlugin, keymap, placeholder as cmPlaceholder } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { autocompletion, startCompletion } from "@codemirror/autocomplete";
import { OverlayScrollbars } from "overlayscrollbars";
import {
  parseQuery,
  serializeQuery,
  impliedGroupRanges,
  nodeSpan,
  tokSpans,
  parseValueContext,
  isValueSuggestField,
  quoteValueIfNeeded,
  suggestFields,
  resolveField,
  QUERY_FIELDS,
} from "../lib/queryLang.js";
import { compileQuery } from "../lib/queryLangCompile.js";
import { splitPipeline, looksLikePipeline, AGG_VERBS, TIME_BUCKETS, RENDER_KEYWORDS } from "../lib/queryPipeline.js";
import { NOTE_CLASS } from "../lib/ui.js";
import FilterBuilder from "./FilterBuilder.jsx";

const DEFAULT_PLACEHOLDER = "Search prompts, \"an exact phrase\", or filter: agent:anthropic  topic:auth  endpoint:*-prod";
const OP_SYM = { ":": ":", "!=": "≠", ">": ">", ">=": "≥", "<": "<", "<=": "≤" };

function asSentence(message) {
  const text = String(message ?? "").trim();
  const capitalized = text.charAt(0).toUpperCase() + text.slice(1);
  return /[.!?]$/.test(capitalized) ? capitalized : `${capitalized}.`;
}

// Tokenize with char offsets (whitespace skipped); raw ranges (incl. a leading
// "-") so pills cover exact text.

function tag(cls, text) { const s = document.createElement("span"); s.className = cls; s.textContent = text; return s; }

// The fields of clauses the host reads itself (the workspace search's
// `review:`), which the query language does not know: drawn as its own are.
const localFieldsFacet = Facet.define({ combine: (lists) => new Set(lists.flat().map((f) => String(f).toLowerCase())) });
const knownField = (view, name) => !!resolveField(name) || view.state.facet(localFieldsFacet).has(String(name).toLowerCase());

// A completed clause, drawn as a fat pill. Atomic (atomicRanges below): caret
// skips it, ⌫ deletes it whole. Click body → edit (caret into clause); ✕ removes.
class ClausePill extends WidgetType {
  constructor(text, from, to) { super(); this.text = text; this.from = from; this.to = to; }
  eq(o) { return o.text === this.text && o.from === this.from && o.to === this.to; }
  toDOM(view) {
    const el = document.createElement("span");
    el.className = this.from === 0 ? "qb-pill qb-lead" : "qb-pill";
    const m = /^(-?)([A-Za-z_][\w-]*)(:|!=|>=|<=|>|<)(.*)$/.exec(this.text);
    if (m && knownField(view, m[2])) {
      if (m[1]) el.appendChild(tag("qb-neg", "−"));
      el.appendChild(tag("qb-f", m[2]));
      el.appendChild(tag("qb-o", OP_SYM[m[3]] || m[3]));
      el.appendChild(tag("qb-v", m[4]));
    } else {
      el.appendChild(tag("qb-text", this.text));
    }
    const x = tag("qb-x", "✕");
    el.appendChild(x);
    el.addEventListener("mousedown", (e) => {
      e.preventDefault();
      if (e.target === x) {
        const doc = view.state.doc;
        const to = doc.sliceString(this.to, this.to + 1) === " " ? this.to + 1 : this.to;
        view.dispatch({ changes: { from: this.from, to } });
      } else {
        view.dispatch({ selection: { anchor: this.to } }); // click → edit
      }
      view.focus();
    });
    return el;
  }
  ignoreEvent() { return false; }
}

class OrPill extends WidgetType {
  constructor(from, to) { super(); this.from = from; this.to = to; }
  eq(o) { return o.from === this.from && o.to === this.to; }
  toDOM(view) {
    const el = tag("qb-or", "OR");
    el.addEventListener("mousedown", (e) => { e.preventDefault(); view.dispatch({ selection: { anchor: this.to } }); view.focus(); });
    return el;
  }
  ignoreEvent() { return false; }
}

// Pipeline stages (everything after a `|>`) — the aggregate/render formula.
// Keyword tokens (verbs, `by`, `top`, time buckets, render kinds) read green;
// the fields/values between them read ink.
const STAGE_KEYWORDS = new Set([
  ...AGG_VERBS, "by", "top", "limit",
  ...Object.keys(TIME_BUCKETS),
  ...Object.keys(RENDER_KEYWORDS),
]);

// A stage token, drawn as a light chip distinct from a filter clause. Click to
// edit (caret in); no ✕ — a stage is a small formula, edited as text.
class StagePill extends WidgetType {
  constructor(text, from, to) { super(); this.text = text; this.from = from; this.to = to; }
  eq(o) { return o.text === this.text && o.from === this.from && o.to === this.to; }
  toDOM(view) {
    const el = document.createElement("span");
    el.className = "qb-stage";
    el.appendChild(tag(STAGE_KEYWORDS.has(this.text.toLowerCase()) ? "qb-sk" : "qb-sv", this.text));
    el.addEventListener("mousedown", (e) => { e.preventDefault(); view.dispatch({ selection: { anchor: this.to } }); view.focus(); });
    return el;
  }
  ignoreEvent() { return false; }
}

// The `|>` stage separator — a subtle arrow; atomic so ⌫ removes it whole.
class PipeWidget extends WidgetType {
  constructor(from, to) { super(); this.from = from; this.to = to; }
  eq(o) { return o.from === this.from && o.to === this.to; }
  toDOM(view) {
    const el = tag("qb-pipe", "▸");
    el.addEventListener("mousedown", (e) => { e.preventDefault(); view.dispatch({ selection: { anchor: this.to } }); view.focus(); });
    return el;
  }
  ignoreEvent() { return false; }
}

// Colour a live (caret-touched) stage token the same as its chip.
function stageColorMarks(tokText, t) {
  return [Decoration.mark({ class: STAGE_KEYWORDS.has(tokText.toLowerCase()) ? "qb-t-sk" : "qb-t-sv" }).range(t.start, t.end)];
}

// Replace each token with a pill EXCEPT one overlapping the selection (the clause
// being edited stays live text). Recomputed on doc/selection change; implied-group
// ranges are cached and only recomputed when the text changes.
// Colour the field/op of the live (being-edited) clause so it matches the pills
// — green field, muted op; value and a leading "-" stay ink.
function clauseColorMarks(view, text, t) {
  const m = /^(-?)([A-Za-z_][\w-]*)(:|!=|>=|<=|>|<)(.*)$/.exec(text.slice(t.start, t.end));
  if (!m || !knownField(view, m[2])) return [];
  const fStart = t.start + m[1].length;
  const oStart = fStart + m[2].length;
  return [
    Decoration.mark({ class: "qb-t-f" }).range(fStart, oStart),
    Decoration.mark({ class: "qb-t-o" }).range(oStart, oStart + m[3].length),
  ];
}

const pillPlugin = ViewPlugin.fromClass(
  class {
    constructor(view) { this.atomic = Decoration.none; this.decorations = this.build(view); }
    build(view) {
      const text = view.state.doc.toString();
      const sel = view.state.selection.main;
      const focused = view.hasFocus;
      // Each token is an atomic pill EXCEPT the one under the caret while the
      // box is being typed in, which stays live text, but coloured (marks) so
      // it reads the same as the pills. Not focused (a search set from
      // elsewhere), every clause is a pill.
      // Precedence grouping is shown beneath (PrecedenceLine) — CM can't box
      // atomic widgets inline.
      const pills = [];
      const marks = [];
      // `|>` splits the query into stages: stage 0 is the filter (field clauses),
      // stages 1+ are the aggregate/render formula (lighter keyword chips). Only
      // for a REAL pipeline — a bare search that merely contains `|>` renders as
      // all-filter, matching the chart / trace-log activation gate.
      const isPipe = looksLikePipeline(text);
      let stage = 0;
      for (const t of tokSpans(text)) {
        if (t.type === "paren") continue;
        const tokText = text.slice(t.start, t.end);
        const touches = focused && sel.from <= t.end && sel.to >= t.start;

        if (isPipe && tokText === "|>") {
          if (touches) marks.push(Decoration.mark({ class: "qb-t-pipe" }).range(t.start, t.end));
          else pills.push(Decoration.replace({ widget: new PipeWidget(t.start, t.end) }).range(t.start, t.end));
          stage++;
          continue;
        }
        if (isPipe && stage > 0) {
          if (touches) marks.push(...stageColorMarks(tokText, t));
          else pills.push(Decoration.replace({ widget: new StagePill(tokText, t.start, t.end) }).range(t.start, t.end));
          continue;
        }
        // Filter stage. "Editing" (stays live text) = caret/selection touches the
        // token — EXCEPT a collapsed caret against a paren (`value)` / `(value`),
        // where the paren delimits the clause so it pills without needing a space.
        const againstParen = sel.empty
          && ((sel.from === t.end && text[t.end] === ")") || (sel.from === t.start && text[t.start - 1] === "("));
        if (touches && !againstParen) {
          if (t.type === "or") marks.push(Decoration.mark({ class: "qb-t-or" }).range(t.start, t.end));
          else marks.push(...clauseColorMarks(view, text, t));
          continue;
        }
        const w = t.type === "or" ? new OrPill(t.start, t.end) : new ClausePill(tokText, t.start, t.end);
        pills.push(Decoration.replace({ widget: w }).range(t.start, t.end));
      }
      this.atomic = Decoration.set(pills, true); // only pills are atomic for cursor motion
      return Decoration.set([...pills, ...marks], true);
    }
    update(u) { if (u.docChanged || u.selectionSet || u.focusChanged) this.decorations = this.build(u.view); }
  },
  {
    decorations: (v) => v.decorations,
    provide: (plugin) => EditorView.atomicRanges.of((view) => view.plugin(plugin)?.atomic || Decoration.none),
  },
);

// A no-op apply for non-selectable status rows (Searching… / No matches).
const NO_APPLY = () => {};

// ── pipeline-stage autocomplete ──
const ARITY1_VERBS = new Set(AGG_VERBS.filter((v) => v !== "count"));
const RENDER_SUGGEST = ["bar", "line", "area", "table", "number", "donut", "treemap", "heatmap", "scatter", "rows", "sparkline", "funnel"];

// Stage of the caret = number of `|>` separators before it (quote-aware).
function pipelineStageAt(text, pos) {
  let stage = 0;
  let inQ = false;
  for (let i = 0; i < pos; i++) {
    const c = text[i];
    if (c === '"') inQ = !inQ;
    else if (!inQ && c === "|" && text[i + 1] === ">") { stage++; i++; }
  }
  return stage;
}

// Completions for the aggregate/render stages: verbs + `by`/`top` at the start of
// the aggregate, fields after an arity-1 verb, dims/time after `by`, and render
// kinds in the render stage (2+).
function pipelineCompletions(context, stage) {
  const word = context.matchBefore(/\w+/);
  if (!word && !context.explicit) return null;
  const from = word ? word.from : context.pos;
  const w = word ? word.text.toLowerCase() : "";
  const seg = context.state.doc.sliceString(0, from);
  const prev = (seg.slice(seg.lastIndexOf("|>") + 2).trim().split(/[\s,]+/).filter(Boolean).pop() || "").toLowerCase();

  let labels;
  if (stage >= 2) labels = RENDER_SUGGEST;
  else if (prev === "by") labels = [...Object.keys(TIME_BUCKETS), ...Object.keys(QUERY_FIELDS)];
  else if (ARITY1_VERBS.has(prev)) labels = Object.keys(QUERY_FIELDS);
  else labels = [...AGG_VERBS, "by", "top"];

  const options = labels
    .filter((l) => !w || l.toLowerCase().startsWith(w))
    .slice(0, 10)
    .map((l) => ({ label: l, apply: l + " " }));
  return options.length ? { from, options, validFor: /^\w*$/ } : null;
}

// Two-mode completion source, reusing the shipped pure helpers. VALUE mode inside
// a `field:`/`field!=` clause; else FIELD names. Value lookups are async + often
// networked, so the dropdown gives explicit loader feedback: a "Searching…" row
// the instant a lookup starts (then results, or a "No matches" row), via a small
// per-(field,term) cache + re-trigger. `cacheRef` is cleared when the scope/range
// (suggestValues identity) changes, so results never go stale.
function makeCompletions(suggestValuesRef, cacheRef, fieldsRef) {
  return (context) => {
    const text = context.state.doc.toString();
    const pos = context.pos;
    // After a `|>`, complete the pipeline vocabulary (verbs / by-dims / render
    // kinds) instead of filter fields/values.
    const stage = pipelineStageAt(text, pos);
    if (stage > 0) return pipelineCompletions(context, stage);
    const vc = parseValueContext(text, pos);
    if (vc) {
      if (!isValueSuggestField(vc.fieldKey)) return null;
      const fn = suggestValuesRef.current;
      if (typeof fn !== "function") return null;
      const base = { from: vc.valueStart, to: pos, filter: false }; // suggester already matched the term
      const key = vc.fieldKey + "\t" + vc.term;
      const cache = cacheRef.current;
      if (cache.has(key)) {
        const cached = cache.get(key);
        if (cached.error) return { ...base, options: [{ label: "Couldn't load " + vc.fieldKey + " values — keep typing to retry", apply: NO_APPLY, boost: -99 }] };
        if (!cached.length) return { ...base, options: [{ label: "No matching " + vc.fieldKey + " values", apply: NO_APPLY, boost: -99 }] };
        return { ...base, options: cached.slice(0, 8).map((r) => ({ label: r.label ?? r.value, detail: r.detail || undefined, apply: quoteValueIfNeeded(r.value) })) };
      }
      // Not cached: fetch in the background; show a "Searching…" row NOW. When it
      // resolves, cache the rows and re-trigger so the row becomes the real results.
      const view = context.view;
      if (cache.size > 200) cache.clear();
      Promise.resolve(fn(vc.fieldKey, vc.term)).then(
        (rows) => { cache.set(key, Array.isArray(rows) ? rows : []); if (view) startCompletion(view); },
        // Cache the FAILURE distinctly (not as []), so a transient error shows a
        // retryable message rather than a misleading "No matching"; a new term retries.
        () => { cache.set(key, { error: true }); if (view) startCompletion(view); },
      );
      return { ...base, options: [{ label: "Searching " + vc.fieldKey + " values…", apply: NO_APPLY, boost: -99, loading: true }] };
    }
    const word = context.matchBefore(/[^\s():!=<>,]+/);
    const w = word ? word.text : "";
    if (!word && !context.explicit) return null;
    if (/[:<>!=,]/.test(w)) return null;
    const fieldOpts = suggestFields(w, fieldsRef.current);
    if (!fieldOpts.length) return null;
    return {
      from: word ? word.from : pos,
      validFor: /^[A-Za-z_]*$/,
      options: fieldOpts.slice(0, 7).map((f) => ({ label: f, detail: QUERY_FIELDS[f]?.desc, apply: f + ":" })),
    };
  };
}

const qbTheme = EditorView.theme({
  // maxWidth keeps it inside its grid column (the host gets min-w-0) so long
  // queries scroll instead of overlapping Sort. Height is NOT forced: a fixed
  // 20px line-height + symmetric 4px padding sizes the editor to ~30px with the
  // field's frame (matching the adjacent Select), the single line vertically
  // centered; fighting CM's align-items:flex-start to center inside a forced
  // height doesn't work (the content fills the height and the line top-aligns
  // within it). The frame is the field's, around the editor and its filter
  // button both.
  "&": { fontSize: "12px", maxWidth: "100%", backgroundColor: "transparent" },
  "&.cm-focused": { outline: "none" },
  // Chips fill the 20px line exactly (height:20 + vertical-align:top, below), so
  // there's no centering offset: 4px above and below the line. Its ends stand in
  // 8px, as the app's other boxed controls (the date picker's px-2) inset their
  // words; a chip that starts the line stands 4px back from that (.qb-lead), as
  // far off the frame at its side as above and below. The page's own type: a
  // query reads as words and chips, as the app does.
  ".cm-content": { fontFamily: "inherit", padding: "4px 8px", caretColor: "var(--ink)", color: "var(--ink)", lineHeight: "20px" },
  ".cm-line": { padding: "0" },
  // CodeMirror's base theme sets its scroller in monospace; the field takes the page's type.
  ".cm-scroller": { overflowX: "auto", overflowY: "hidden", fontFamily: "inherit" },
  ".cm-placeholder": { color: "var(--muted-soft)" },
  ".cm-selectionBackground, &.cm-focused .cm-selectionBackground": { backgroundColor: "color-mix(in srgb, var(--green) 25%, transparent) !important" },
  // pills
  // height == line-height (20px) + vertical-align:top makes the chip fill the line
  // exactly — no baseline-centering offset, so no translate hack needed.
  ".qb-pill": { display: "inline-flex", alignItems: "center", verticalAlign: "top", height: "20px", boxSizing: "border-box", gap: "1px", border: "1px solid var(--line)", background: "var(--control-bg)", borderRadius: "2px", padding: "0 3px 0 7px", margin: "0", cursor: "pointer", lineHeight: "1" },
  ".qb-pill.qb-lead": { marginLeft: "-4px" },
  ".qb-f": { color: "var(--green)" },
  ".qb-o": { color: "var(--muted)" },
  ".qb-v": { color: "var(--ink)" },
  // live (being-edited) clause — same colours as a pill, minus the box/✕
  ".qb-t-f": { color: "var(--green)" },
  ".qb-t-o": { color: "var(--muted)" },
  ".qb-t-or": { color: "var(--green)" },
  ".qb-text": { color: "var(--ink)" },
  ".qb-neg": { color: "var(--ink)", fontWeight: "600", marginRight: "2px" },
  ".qb-x": { marginLeft: "4px", color: "var(--muted)", padding: "0 2px", cursor: "pointer" },
  ".qb-x:hover": { color: "var(--ink)" },
  ".qb-or": { display: "inline-flex", alignItems: "center", verticalAlign: "top", height: "20px", boxSizing: "border-box", background: "var(--green-soft)", color: "var(--green)", borderRadius: "2px", padding: "0 5px", margin: "0", fontWeight: "600", fontSize: "10px", letterSpacing: "0.04em", cursor: "pointer" },
  // pipeline stages (after `|>`): a lighter "formula" chip + a subtle arrow separator
  ".qb-stage": { display: "inline-flex", alignItems: "center", verticalAlign: "top", height: "20px", boxSizing: "border-box", border: "1px solid var(--line-soft)", background: "transparent", borderRadius: "2px", padding: "0 6px", margin: "0", cursor: "pointer", lineHeight: "1" },
  ".qb-sk": { color: "var(--green)" },
  ".qb-sv": { color: "var(--ink)" },
  ".qb-pipe": { display: "inline-flex", alignItems: "center", verticalAlign: "top", height: "20px", color: "var(--muted-soft)", padding: "0 2px", margin: "0", cursor: "pointer" },
  ".qb-t-sk": { color: "var(--green)" },
  ".qb-t-sv": { color: "var(--ink)" },
  ".qb-t-pipe": { color: "var(--muted-soft)" },
  // autocomplete dropdown — match the dashboard panel; kill CM's default italic detail
  ".cm-tooltip.cm-tooltip-autocomplete": { backgroundColor: "var(--panel)", border: "1px solid var(--line)", borderRadius: "2px", boxShadow: "0 6px 24px rgba(0,0,0,0.18)" },
  ".cm-tooltip-autocomplete > ul > li": { fontFamily: "inherit", fontSize: "12px", padding: "4px 9px", color: "var(--ink)", display: "flex", gap: "8px" },
  ".cm-tooltip-autocomplete > ul > li[aria-selected]": { backgroundColor: "var(--green-soft)", color: "var(--ink)" },
  ".cm-completionLabel": { color: "var(--green)" },
  ".cm-completionDetail": { fontStyle: "normal", color: "var(--muted)", marginLeft: "auto" },
});

// Single line only: drop any transaction that introduces a newline.
const singleLine = EditorState.transactionFilter.of((tr) => (tr.newDoc.lines > 1 ? [] : tr));

// Read-only "binds as" line beneath the editor: makes precedence explicit by
// drawing green parens around each AND-run that's an operand of an OR. Renders
// only when AND + OR are mixed (otherwise grouping is obvious). This is where
// precedence lives — CM can't box atomic pill widgets inline.
// Render the parse tree with EXPLICIT `AND` (muted) and `OR` (green) + parens, so
// the binding is unambiguous. An implied AND-group (an AND-run that's an operand
// of an OR) gets green parens — the precedence that isn't otherwise visible.
function renderBinds(node, parentType, onJump) {
  if (!node) return null;
  // Each filter leaf is a link: click to select + scroll to its chip in the editor.
  if (node.type === "pred" || node.type === "text") {
    const span = nodeSpan(node);
    return (
      <button
        type="button"
        onClick={() => span && onJump?.(span)}
        className="qb-binds-jump inline align-baseline cursor-pointer rounded-[2px] px-[1px] hover:text-[color:var(--green)] hover:underline underline-offset-2 focus:outline-none focus-visible:text-[color:var(--green)] focus-visible:underline"
      >
        {serializeQuery(node)}
      </button>
    );
  }
  if (node.type === "error") return serializeQuery(node);
  if (node.type === "not") return <>NOT {renderBinds(node.child, "not", onJump)}</>;
  const inner = node.children.map((c, i) => (
    <Fragment key={i}>
      {i > 0 && (node.type === "or"
        ? <span className="text-[color:var(--green)] font-semibold"> OR </span>
        : <span className="text-[color:var(--muted-soft)]"> AND </span>)}
      {renderBinds(c, node.type, onJump)}
    </Fragment>
  ));
  if (parentType === null) return inner; // root — no wrapping parens
  const green = node.type === "and" && parentType === "or"; // implied precedence group
  const pcls = "font-semibold " + (green ? "text-[color:var(--green)]" : "text-[color:var(--ink)]");
  return <><span className={pcls}>(</span>{inner}<span className={pcls}>)</span></>;
}

function PrecedenceLine({ value, onJump }) {
  const { ast } = useMemo(() => parseQuery(value), [value]);
  const ranges = useMemo(() => impliedGroupRanges(ast), [ast]);
  if (!ranges.length) return null; // only when AND + OR are mixed (precedence non-obvious)
  return (
    <div className="text-[11px] leading-[1.3]">
      <span className="uppercase tracking-[0.08em] text-[10px] text-[color:var(--muted-soft)] mr-[6px]">binds as</span>
      <span className="text-[color:var(--muted)]">{renderBinds(ast, null, onJump)}</span>
    </div>
  );
}

// `onSubmit`: what Enter does with the query, when a suggestion is not being
// picked (the suggestions take Enter while their list is open).
// `withoutLocal(text)`: the filter without the clauses the host applies
// itself (the workspace search's review clauses), which the query language
// does not know; its notes are about the rest. `localFields`: those clauses'
// fields, drawn as chips as the language's own are (read as the box opens).
export default function QueryBar({ value = "", onChange, onSubmit = null, suggestValues, placeholder = DEFAULT_PLACEHOLDER, label = "Search", fields = null, notes = null, withoutLocal = null, localFields = null }) {
  const hostRef = useRef(null);
  const filterBtnRef = useRef(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const viewRef = useRef(null);
  const osRef = useRef(null); // OverlayScrollbars on the CM scroller — floating, auto-hide, no reserved gutter
  const onChangeRef = useRef(onChange);
  const onSubmitRef = useRef(onSubmit);
  const suggestValuesRef = useRef(suggestValues);
  // Field allowlist for autocomplete (null = all). Held in a ref so changing it
  // doesn't tear down + rebuild the once-created editor.
  const fieldsRef = useRef(fields);
  const cacheRef = useRef(new Map()); // (field\tterm) → value rows; cleared when scope/range changes
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onSubmitRef.current = onSubmit; }, [onSubmit]);
  useEffect(() => { fieldsRef.current = fields; }, [fields]);
  useEffect(() => { suggestValuesRef.current = suggestValues; cacheRef.current.clear(); }, [suggestValues]);

  // Diagnostics + precedence describe the FILTER only.
  const filterText = useMemo(() => (looksLikePipeline(value) ? splitPipeline(value)[0] : value), [value]);
  const checked = useMemo(() => (withoutLocal ? withoutLocal(filterText) : filterText), [withoutLocal, filterText]);
  const { ast, warnings: parseWarnings } = useMemo(() => parseQuery(checked), [checked]);
  const { errors, warnings: compileWarnings } = useMemo(() => compileQuery(ast), [ast]);
  const diagnostics = useMemo(() => {
    const seen = new Set(); const out = [];
    const push = (level, message) => { const k = `${level}:${message}`; if (seen.has(k)) return; seen.add(k); out.push({ level, message }); };
    for (const e of errors) push("error", e.message);
    const sentences = [...new Set([...(notes ?? []), ...parseWarnings, ...compileWarnings].map((w) => asSentence(w.message)))];
    if (sentences.length) out.push({ level: "note", message: sentences.join(" ") });
    return out;
  }, [errors, parseWarnings, compileWarnings, notes]);

  useLayoutEffect(() => {
    if (!hostRef.current) return undefined;
    // Coalesce OverlayScrollbars re-measures to one per frame: a long query typed
    // fast fires many doc changes; OS's own observers catch most, this is insurance.
    let osRaf = 0;
    const scheduleOsUpdate = () => {
      if (osRaf) return;
      osRaf = window.requestAnimationFrame(() => { osRaf = 0; osRef.current?.update(); });
    };
    const state = EditorState.create({
      doc: value || "",
      extensions: [
        history(),
        localFieldsFacet.of(localFields || []),
        pillPlugin,
        autocompletion({
          override: [makeCompletions(suggestValuesRef, cacheRef, fieldsRef)],
          icons: false,
          activateOnTyping: true,
          // pulsing dot in front of the "Searching…" status row, for clear loader motion
          addToOptions: [{
            position: 10,
            render: (completion) => {
              if (!completion.loading) return null;
              const dot = document.createElement("span");
              dot.className = "qb-ac-dot";
              return dot;
            },
          }],
        }),
        qbTheme,
        singleLine,
        cmPlaceholder(placeholder || ""),
        EditorView.contentAttributes.of({ "aria-label": "Query filter" }),
        keymap.of([
          { key: "Enter", run: (view) => { const submit = onSubmitRef.current; if (!submit) return false; submit(view.state.doc.toString()); return true; } },
          ...defaultKeymap,
          ...historyKeymap,
        ]),
        EditorView.updateListener.of((u) => { if (u.docChanged) { onChangeRef.current?.(u.state.doc.toString()); scheduleOsUpdate(); } }),
      ],
    });
    const view = new EditorView({ state, parent: hostRef.current });
    viewRef.current = view;
    // Overlay scrollbars on CM's own scroller — pass it as the viewport so OS draws
    // its floating, auto-hiding bar without reparenting CM's DOM (the native bar
    // reserved a gutter row that made the box taller). os-theme-origin matches the app.
    osRef.current = OverlayScrollbars(
      { target: view.dom, elements: { viewport: view.scrollDOM } },
      {
        scrollbars: { autoHide: "leave", autoHideDelay: 160, theme: "os-theme-origin" },
        overflow: { x: "scroll", y: "hidden" },
      },
    );
    scheduleOsUpdate();
    return () => {
      if (osRaf) window.cancelAnimationFrame(osRaf);
      osRef.current?.destroy(); osRef.current = null;
      view.destroy(); viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current === (value || "")) return;
    view.dispatch({ changes: { from: 0, to: current.length, insert: value || "" } });
  }, [value]);

  // Click a filter in the "binds as" line → select its clause and scroll the
  // editor to it. Selecting also un-pills that clause to live text, so it reads
  // as highlighted (green selection bg) — making clear which chip you jumped to.
  const jumpToSpan = useCallback((span) => {
    const view = viewRef.current;
    if (!view || !span) return;
    const len = view.state.doc.length;
    const from = Math.max(0, Math.min(span[0], len));
    const to = Math.max(from, Math.min(span[1], len));
    view.dispatch({ selection: { anchor: from, head: to }, scrollIntoView: true });
    view.focus();
  }, []);

  return (
    <div className="grid min-w-0 grid-cols-1 gap-[5px]">
      {label ? <span className={NOTE_CLASS}>{label}</span> : null}
      {/* The root's minmax track contains long diagnostics; this inner min-w-0
          lets CodeMirror and the filter button share that constrained width.
          One field: the editor and its filter button in one frame, the button
          a cell against it, lit while its builder is open. */}
      <div className="flex min-w-0 items-stretch rounded-[2px] border border-[color:var(--line)] transition-colors focus-within:border-[color:var(--line-strong)]" data-query-field="">
        <div ref={hostRef} className="qb-host min-w-0 flex-1" />
        <HoverTooltip placement="bottom" label="Build a filter">
          <button
            ref={filterBtnRef}
            type="button"
            aria-label="Build a filter"
            {...phAttrs({ [ATTRIBUTE.ACTION]: ACTION.FILTER_BUILDER })}
            aria-expanded={builderOpen}
            onClick={() => setBuilderOpen((v) => !v)}
            className={[
              "grid w-[30px] shrink-0 place-items-center border-l border-[color:var(--line-soft)] transition-colors [&_svg]:block [&_svg]:h-4 [&_svg]:w-4",
              builderOpen ? "[background-image:linear-gradient(var(--green-soft),var(--green-soft))] text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]",
            ].join(" ")}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 4h12M4 8h8M6.5 12h3" />
            </svg>
          </button>
        </HoverTooltip>
      </div>
      {builderOpen && (
        <FilterBuilder
          query={value}
          suggestValues={suggestValues}
          fields={fields}
          anchorRef={filterBtnRef}
          onApply={(q) => onChange?.(q)}
          onClose={() => setBuilderOpen(false)}
        />
      )}
      <PrecedenceLine value={filterText} onJump={jumpToSpan} />
      {diagnostics.length > 0 && (
        <div className="grid gap-[3px]">
          {diagnostics.map((d, i) => (
            <div key={i} className="flex min-w-0 items-baseline gap-[7px] text-[11px] leading-[1.3]">
              <span className={"uppercase tracking-[0.08em] text-[10px] " + (d.level === "error" ? "text-[color:var(--ink)] font-semibold" : "text-[color:var(--eyebrow)]")}>{d.level}</span>
              <span className="min-w-0 break-words text-[color:var(--muted)]">{d.message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
